"use client";

import { GoogleAnalytics, GoogleTagManager } from "@next/third-parties/google";
import { useEffect, useState } from "react";

interface DeferredThirdPartyProps {
  gtmId: string;
  gaId: string;
}

const INTERACTION_EVENTS = [
  "scroll",
  "click",
  "touchstart",
  "keydown",
  "mousemove",
] as const;
// Real users who never scroll/click/touch within this window (bots, very
// short bounces) still get tagged - this is the ceiling on how "deferred"
// loading gets, not how long a typical visit takes to trigger it.
const FALLBACK_DELAY_MS = 3500;

/**
 * Mounts GTM/GA only after the first real user interaction (or a timeout
 * fallback), instead of on every page load via next/script's
 * "afterInteractive" strategy. GTM's own container script triggers further
 * gtag.js loads for whatever tags are configured inside it (GA4, Ads
 * conversion, etc.) - deferring this mount defers that whole cascade, which
 * was the single largest chunk of third-party main-thread time in
 * Lighthouse's mobile trace (~290ms across the three scripts). Trade-off:
 * a session that bounces before interacting and before the fallback timer
 * fires won't be tagged at all, versus always being tagged (late) before.
 */
export default function DeferredThirdParty({
  gtmId,
  gaId,
}: DeferredThirdPartyProps) {
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    if (shouldLoad) return;


    const trigger = () => {
      setShouldLoad(true);
    };

    const timeoutId = setTimeout(trigger, FALLBACK_DELAY_MS);

    INTERACTION_EVENTS.forEach((event) =>
      window.addEventListener(event, trigger, { passive: true, once: true }),
    );

    return () => {
      clearTimeout(timeoutId);
      INTERACTION_EVENTS.forEach((event) =>
        window.removeEventListener(event, trigger),
      );
    };
  }, [shouldLoad]);

  if (!shouldLoad) return null;

  return (
    <>
      <GoogleTagManager gtmId={gtmId} />
      <GoogleAnalytics gaId={gaId} />
    </>
  );
}
